'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';

const aboutSections = [
  { id: 'about', label: 'About Us', href: '/about' },
  { id: 'history', label: 'History', href: '/about/history' },
  { id: 'mission', label: 'Mission', href: '/about/mission' },
  { id: 'vision', label: 'Vision', href: '/about/vision' },
  { id: 'membership', label: 'Membership', href: '/about/membership' },
  { id: 'quality', label: 'Quality Management', href: '/about/quality-management' },
  { id: 'ethics', label: 'Business Ethics', href: '/about/business-ethics' },
];

const principles = [
  {
    category: 'Human Rights',
    color: '#1B6B8A',
    icon: '🤝',
    items: [
      { num: 1, text: 'Businesses should support and respect the protection of internationally proclaimed human rights.' },
      { num: 2, text: 'To make sure that they are not complicit in human rights abuses.' },
    ],
  },
  {
    category: 'Labour',
    color: '#2A8FAB',
    icon: '👷',
    items: [
      { num: 3, text: 'Businesses should uphold the freedom of association and the effective recognition of the right to collective bargaining.' },
      { num: 4, text: 'The elimination of all forms of forced and compulsory labour.' },
      { num: 5, text: 'The effective abolition of child labour.' },
      { num: 6, text: 'The elimination of discrimination in respect of employment and occupation.' },
    ],
  },
  {
    category: 'Environment',
    color: '#1A7A4A',
    icon: '🌱',
    items: [
      { num: 7, text: 'Businesses should support a precautionary approach to environmental challenges.' },
      { num: 8, text: 'To undertake initiatives to promote greater environmental responsibility.' },
      { num: 9, text: 'To encourage the development and diffusion of environmentally friendly technologies.' },
    ],
  },
  {
    category: 'Anti-Corruption',
    color: '#C8922A',
    icon: '⚖️',
    items: [
      { num: 10, text: 'Businesses should work against corruption in all its forms, including extortion and bribery.' },
    ],
  },
];

export default function BusinessEthicsPage() {
  return (
    <>
      <Header />
      <main className="min-h-screen" style={{ background: '#F4F6F9', color: '#0F2340' }}>
        {/* Hero */}
        <div className="relative pt-32 pb-16 px-6" style={{ background: 'linear-gradient(180deg, #0F2340 0%, #1A3558 100%)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="max-w-5xl mx-auto">
            <Link href="/about" className="inline-flex items-center gap-2 text-sm font-body mb-4 transition-colors" style={{ color: '#7EB8F7' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#7EB8F7'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
              The Company
            </Link>
            <p className="text-xs uppercase tracking-widest font-body mb-3" style={{ color: '#7EB8F7' }}>World Transport Overseas</p>
            <h1 className="text-4xl lg:text-5xl font-bold font-body mb-4" style={{ color: '#FFFFFF' }}>Business Ethics</h1>
            <p className="text-lg font-body max-w-2xl" style={{ color: 'rgba(200,216,240,0.7)' }}>
              Member of the UN Global Compact since 2013 — committed to responsible and ethical business.
            </p>
          </div>
        </div>
        {/* Sub-nav */}
        <div className="sticky top-20 z-30 px-6 py-3" style={{ background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid rgba(15,35,64,0.1)', backdropFilter: 'blur(12px)' }}>
          <div className="max-w-5xl mx-auto flex flex-wrap gap-1">
            {aboutSections?.map((s) => (
              <Link key={s?.id} href={s?.href}
                className="px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-200"
                style={s?.id === 'ethics'
                  ? { background: '#1B6B8A', color: '#FFFFFF', border: '1px solid rgba(27,107,138,0.5)' }
                  : { background: 'transparent', color: '#3A5070', border: '1px solid rgba(15,35,64,0.15)' }}
                onMouseEnter={(e) => { if (s?.id !== 'ethics') { e.currentTarget.style.background = 'rgba(27,107,138,0.1)'; e.currentTarget.style.color = '#0F2340'; } }}
                onMouseLeave={(e) => { if (s?.id !== 'ethics') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#3A5070'; } }}
              >{s?.label}</Link>
            ))}
          </div>
        </div>
        {/* Content */}
        <div className="max-w-5xl mx-auto px-6 py-16">
          {/* UN Global Compact intro */}
          <div className="rounded-3xl p-8 mb-12" style={{ background: '#0F2340', border: '1px solid rgba(27,107,138,0.3)' }}>
            <div className="flex items-start gap-5">
              <div className="text-4xl flex-shrink-0">🌍</div>
              <div>
                <h2 className="text-xl font-bold font-body mb-3" style={{ color: '#FFFFFF' }}>UN Global Compact Member Since 2013</h2>
                <p className="text-sm font-body leading-relaxed mb-3" style={{ color: 'rgba(200,216,240,0.85)' }}>
                  Since 2013, World Transport Overseas is a member of the largest voluntary corporate responsibility initiative in the world with over 12,000 corporate participants and other stakeholders from over 145 countries — the United Nations Global Compact.
                </p>
                <p className="text-sm font-body leading-relaxed" style={{ color: 'rgba(200,216,240,0.85)' }}>
                  The Global Compact is a platform for knowledge exchange and dialogue with the active participation and support from companies, UN organisations, business associations, NGOs and trade unions to promote social responsibility and ethical business standards. Through its commitment to generally accepted values, WTO minimises the negative impacts of its business operations and contributes to making globalisation a positive force.
                </p>
              </div>
            </div>
          </div>

          {/* 10 Principles */}
          <h2 className="text-2xl font-bold font-body mb-8" style={{ color: '#0F2340' }}>The Ten Principles</h2>
          <div className="space-y-6">
            {principles?.map((cat) => (
              <div key={cat?.category} className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: `1px solid ${cat?.color}30`, boxShadow: '0 2px 8px rgba(15,35,64,0.06)' }}>
                <div className="px-6 py-4 flex items-center gap-3" style={{ background: `${cat?.color}10`, borderBottom: `1px solid ${cat?.color}20` }}>
                  <span className="text-2xl">{cat?.icon}</span>
                  <h3 className="text-base font-bold font-body" style={{ color: cat?.color }}>{cat?.category}</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {cat?.items?.map((item) => (
                    <div key={item?.num} className="px-6 py-4 flex gap-4 items-start">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold font-body" style={{ background: `${cat?.color}15`, color: cat?.color, border: `1px solid ${cat?.color}30` }}>
                        {item?.num}
                      </span>
                      <p className="text-sm font-body leading-relaxed pt-1" style={{ color: '#3A5070' }}>{item?.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Anti-Corruption Policy note */}
          <div className="mt-10 rounded-2xl p-6" style={{ background: '#FFFFFF', border: '1px solid rgba(200,146,42,0.3)', boxShadow: '0 2px 8px rgba(15,35,64,0.06)' }}>
            <h3 className="text-base font-bold font-body mb-2" style={{ color: '#C8922A' }}>Anti-Corruption and Anti-Bribery Policy</h3>
            <p className="text-sm font-body leading-relaxed" style={{ color: '#3A5070' }}>
              World Transport Overseas maintains a strict Anti-Corruption and Anti-Bribery Policy in line with Principle 10 of the UN Global Compact. All employees, partners, and agents are expected to uphold the highest standards of integrity in all business dealings.
            </p>
          </div>
        </div>
      </main>
    </>
  );
}
